/**
 * EngageTrack React Hook
 */

import { useCallback } from 'react';
import type { UseEngageTrackReturn } from '../types';
import { useEngageTrackContext } from '../context/EngageTrackContext';

/**
 * Main hook for tracking events and accessing session state
 * @returns Tracking functions and state
 * 
 * @example
 * ```tsx
 * const { track, trackReferralConversion, getSessionState } = useEngageTrack();
 * 
 * // Track a custom event
 * track('button_click', { buttonName: 'Sign Up' });
 * 
 * // Track a referral conversion
 * trackReferralConversion({ plan: 'premium' });
 * 
 * // Get session state
 * const session = getSessionState();
 * console.log('Session ID:', session.sessionId);
 * ```
 */
export const useEngageTrack = (): UseEngageTrackReturn => {
  const context = useEngageTrackContext();

  const track = useCallback(
    (eventType: string, data?: Record<string, unknown>) => {
      context.track(eventType, data);
    },
    [context]
  );

  const trackReferralConversion = useCallback(
    (data?: Record<string, unknown>) => {
      context.trackReferralConversion(data);
    },
    [context]
  );

  const getSessionState = useCallback(() => {
    return context.getSessionState();
  }, [context]);

  return {
    track,
    trackReferralConversion,
    getSessionState,
    isEnabled: !context.config?.disabled,
    isInitialized: context.isInitialized,
  };
};
